import { DtoCacheDataSourceFromId } from "@cervval.js/common-action/src/dtos/dtoCacheDataSourceFromId";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { DataSource, DataSourceItem } from "@cervval.js/common/src/forms/dataSource";
import { ObjectDataSource } from "@cervval.js/common/src/forms/objectDataSource";
import { DATA_SOURCE_SERVICE } from "@cervval.js/common/src/services/dataSource.service";
import { DefaultDataSourceService } from "@cervval.js/common/src/services/dataSourceService.impl/defaultDataSourceService";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { anyReallyAny, setProp } from "@cervval.js/common/src/tools/any.tools";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { DataSourceRefreshOptions } from "@ehealth_tools/common/src/datasources/dataSource.definitions";

export type DataSourceFormat<T extends number = number> = {
    name: string,
    id: T
}

/** */
export function getDataSourceItems<ElementTypes, AppTableName extends keyof ElementTypes & string, DataSourceUID extends string, T = unknown>(uid: DataSourceUID): DataSourceItem<T>[] {
    const dataSourceService = injector.get<AbstractDataSourceService<ElementTypes, AppTableName, DataSourceUID>>(DATA_SOURCE_SERVICE);
    return (dataSourceService.getDataSource(uid)?.getItems() ?? []) as DataSourceItem<T>[];
}

export type DatasourceExtraField<DataSourceUID extends string> = `__datasource-${DataSourceUID}`


@Injectable(DATA_SOURCE_SERVICE)
export abstract class AbstractDataSourceService<ElementTypes, AppTableName extends keyof ElementTypes & string, DataSourceUID extends string> extends DefaultDataSourceService {

    private _datasourceOptions: Partial<Record<DataSourceUID, DataSourceRefreshOptions<ElementTypes, anyReallyAny, anyReallyAny, anyReallyAny>>> = {};

    constructor() {
        super();
        this._init();
    }

    /** Refresh the given datasource */
    public refresh(uid: DataSourceUID, force = false): void {
        if (!force && uid.indexOf("__") >= 0) { return; } // Skip static

        const options = this._datasourceOptions[uid];
        if (options == null) { getLogConsole().error("Datasource " + uid + " does not exists"); return; }
        const optionDto = options.source.dto;
        if (optionDto) {
            // DTOCacheDataSource does not need hard refresh
            if (force) {
                const dtoDataSource = new DtoCacheDataSourceFromId<ElementTypes, AppTableName>(options.source.dto.table, options.labeler, options.source.dto.filter);
                this.addDataSource(uid, dtoDataSource);
            }
        } else {
            const dtos = options.source.data;
            dtos.forEach(dto => setProp(dto, "__datasource-" + uid, options.labeler(dto)));
            const dataSourceToAdd = new ObjectDataSource(dtos, options.idField as keyof ElementTypes[AppTableName] & string, "__datasource-" + uid);
            this.addDataSource(uid, dataSourceToAdd);
        }

    }

    /** Refresh all the datasources */
    public refreshAllIfNeeded(): void {
        for (const uid of objectKeys(this._datasourceOptions)) {
            this.refresh(uid);
        }
    }

    public refreshThem(uids: DataSourceUID[]): void {
        for (const uid of uids) {
            this.refresh(uid);
        }
    }

    /** 
     * If the uid is dynamic (++) : refresh the datasource before returning the data (Always Fresh ©) 
     */
    public override getDataSource<T = unknown, V = unknown, DataSourceContext = unknown>(uid: DataSourceUID): DataSource<T, V, DataSourceContext> | undefined {
        return super.getDataSource(uid);
    }

    /** Register option for datasource and build it */
    public registerDataSource<TableName extends AppTableName | undefined, T extends (TableName extends AppTableName ? ElementTypes[TableName] : anyReallyAny)>(uid: DataSourceUID, options: DataSourceRefreshOptions<ElementTypes, AppTableName, TableName, T>, overwrite = false): void {
        if (!overwrite && this._datasourceOptions[uid]) { return; }
        this._datasourceOptions[uid] = options;
        this.refresh(uid, true);
    }

    /** */
    protected _init(): void {
        // STATICS
        // this.registerDataSource(DataSourceUID.TAG_SCOPES, {
        //     source: {
        //         data: [
        //             { id: TagScope.COLLABORATORS, name: "Collaborateurs" },
        //             { id: TagScope.COSTS, name: "Dépenses" },
        //             { id: TagScope.SERVICES, name: "Organismes/Services" },
        //             { id: TagScope.COST_RATES, name: "Taux Horaires" }
        //         ]
        //     },
        //     idField: "id",
        //     labeler: (data) => data.name
        // });
    }
} 